import { ProductDetailsModalContextProvider } from '@app/components/products/ProductDetails/context/ProductDetailsModalContext';
import { ProductDetailsModal } from '@app/components/products/ProductDetails/ProductDetailsModal';
import { Notification } from '@app/components/various/Notification';
import { PageTitle } from '@app/components/various/PageTitle';
import { getAllDeliveryWindows, getIsDeliveryWindowsLoading } from '@app/ducks';
import { getCancelledOrderProducts, getIsOrderCheckoutSuccess, getOrderDeliveryWindowsIds, getOrderDetails } from '@app/ducks/order';
import { getDeliveryWindowsByIds } from '@app/logic/deliveryWindows';
import { getIsOrderClosed, getOrderCancelDate, getOrderPreferredShippingDate, getUnitsCountInDelwin } from '@app/logic/Orders';
import { useUrlProductId } from '@app/utils/hooks';
import { useAvailableOrderDetails } from '@app/utils/hooks/useAvailableOrderDetails';
import { useUrlVariantId } from '@app/utils/hooks/useUrlVariantId';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Order } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CheckoutContentSkeleton } from '../CheckoutContent/CheckoutContentSkeleton';
import { CheckoutOrdersSplit } from '../CheckoutProducts';
import { CheckoutSubtotal } from '../CheckoutSubtotal';
import { CheckoutSummary } from '../CheckoutSummary';
import { CheckoutThankMessage } from '../CheckoutThankMessage';
import { SplitNotification } from '../SplitNotification';

import { CancelledOrderProductsAlert } from './CancelledOrderProductsAlert';
import { CheckoutSuccessAdditionalInformation } from './CheckoutSuccessAdditionalInformation';
import { CheckoutSuccessAttachments } from './CheckoutSuccessAttachments';
import { CheckoutSuccessCancelledProducts } from './CheckoutSuccessCancelledProducts';
import { CheckoutSuccessPayments } from './CheckoutSuccessPayments';
import { CheckoutSuccessPoNumber } from './CheckoutSuccessPoNumber';
import { CheckoutSuccessShipments } from './CheckoutSuccessShipments';
import { CheckoutSuccessShippingAddress } from './CheckoutSuccessShippingAddress';
import { CheckoutSuccessShippingPreferences } from './CheckoutSuccessShippingPreferences';

export const CheckoutSingleSuccess = () => {
  const { t } = useTranslation(['checkout', 'common', 'selections']);
  const isOrderCheckoutSuccess = useSelector(getIsOrderCheckoutSuccess);
  const orderDeliveryWindowIds = useSelector(getOrderDeliveryWindowsIds);
  const isDeliveryWindowsLoading = useSelector(getIsDeliveryWindowsLoading);
  const allDeliveryWindows = useSelector(getAllDeliveryWindows);
  const orderDetails = useSelector(getOrderDetails);
  const productsWithFullyCancelledItems = useSelector(getCancelledOrderProducts);
  const productId = useUrlProductId();
  const variantId = useUrlVariantId();
  const { order } = orderDetails;
  const { orderNumber } = useAvailableOrderDetails();

  const isLoadingOrder = !isDefined(orderDetails.order.order) || isDeliveryWindowsLoading;
  const deliveryWindows = getDeliveryWindowsByIds(allDeliveryWindows, orderDeliveryWindowIds);
  const hasOrderId = isDefined(order.order);
  const hasUnits = deliveryWindows.some(({ deliveryWindow }) => getUnitsCountInDelwin(deliveryWindow, order.products) > 0);
  const preferredShippingDate = getOrderPreferredShippingDate(order);
  const cancelDate = getOrderCancelDate(order);
  const shouldShowAttachmentsNotification = getIsOrderClosed(order) && !isEmpty(order.attachments ?? []);

  const orderProducts = React.useMemo(
    () => (Array.isArray(orderDetails.products) ? [] : Object.values(orderDetails.products)),
    [orderDetails.products],
  );

  const someProductAreUnavailable = order.products.some(
    (orderProduct: Order.Product) => !orderProducts.find(product => product.product === orderProduct.product),
  );

  return (
    <PageTitle title={t('checkout:success_page_title', { orderNumber })}>
      <div className="mx-auto w-full max-w-screen-xl px-4 pb-16 lg:px-8">
        <div className="flex w-full flex-col gap-2 pt-4 empty:hidden">
          {isOrderCheckoutSuccess && <CheckoutThankMessage orderDetails={orderDetails} />}
          <SplitNotification messageType="general" order={order} />
          <CancelledOrderProductsAlert hasProducts={hasUnits} />
          {shouldShowAttachmentsNotification && (
            <Notification type="info" message={t('checkout:attachments_info', { count: order.attachments.length })} />
          )}
        </div>
        {isLoadingOrder ?
          <CheckoutContentSkeleton />
        : <div className="pt-8">
            <div className="mb-8 border-b border-neutral-90 pb-8">
              <CheckoutSummary orderDetails={orderDetails} />
              {someProductAreUnavailable && <p className="text-neutral-40 body-4">{t('checkout:some_products_are_unavailable')}</p>}
            </div>

            <div className="flex w-full flex-col gap-8">
              {(hasUnits || !hasOrderId) && <CheckoutOrdersSplit orderDetails={orderDetails} />}
              <div className="grid gap-x-20 gap-y-8 lg:grid-cols-[1fr_20rem]">
                <div className="flex w-full flex-col gap-8">
                  <CheckoutSuccessCancelledProducts cancelledProducts={productsWithFullyCancelledItems} products={orderProducts} />
                  <CheckoutSuccessPoNumber poNumber={order.poNumber} />
                  <CheckoutSuccessShippingPreferences preferredShippingDate={preferredShippingDate} cancelDate={cancelDate} />
                  {isDefined(order.shippingAddress) && <CheckoutSuccessShippingAddress address={order.shippingAddress} />}
                  <CheckoutSuccessAdditionalInformation
                    additionalNotes={order.additionalNotes}
                    estimatedShippingDate={order.estimatedShippingDate}
                  />
                  {getIsOrderClosed(order) && (
                    <>
                      <CheckoutSuccessShipments shipments={order.shipments} />
                      <CheckoutSuccessPayments payments={order.payments} order={order} />
                      <CheckoutSuccessAttachments attachments={order.attachments} />
                    </>
                  )}
                </div>
                <div>
                  <div className="z-topBar lg:sticky lg:top-[--headerHeight]">
                    <CheckoutSubtotal isReadOnly />
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      </div>
      <ProductDetailsModalContextProvider productId={productId} products={orderProducts} variantId={variantId}>
        <ProductDetailsModal />
      </ProductDetailsModalContextProvider>
    </PageTitle>
  );
};
